/**
 * idbBloatDetector — close the chandler-pattern reload loop.
 *
 * Why this exists
 * ───────────────
 * `wsSizeBudget` already prevents an in-memory bloated Y.Doc from being
 * uploaded to the server. But the IndexedDB store itself is unaffected:
 * the same bloated state is reloaded on every page refresh, which means
 * the user is permanently locked out of that room until they manually
 * clear site data.
 *
 * This module evaluates the freshly-IDB-loaded state size against the
 * same per-prefix hard limits the size budget uses. If the local IDB
 * state already exceeds what the server will accept for this room type,
 * we clear the IDB so the next page load starts clean (the WS will
 * stream a fresh canonical snapshot from the server).
 *
 * What it does NOT do
 * ───────────────────
 *   - It does not destroy the in-memory Y.Doc. The current session
 *     keeps whatever state IDB loaded; `wsSizeBudget` will hard-stop
 *     the provider, the banner will fire, the user reloads, and the
 *     fresh IDB takes effect on the next load.
 *   - It does not touch IDB while loading — only after `idb.synced`
 *     fires and we have a final size measurement.
 *
 * Per-prefix thresholds
 * ─────────────────────
 * Mirror `wsSizeBudget`'s hard limits. Anything at or above the hard
 * limit cannot be uploaded successfully, so there is no point keeping
 * it in IDB.
 *
 * Reference: WS robustness plan, Phase 4b (chandler-pattern recovery).
 */

import * as SentryDefault from '@sentry/react';
import { createFileLogger } from '../../utils/logger';
import { broadcastSyntheticHardStop } from './wsHardStop';

const log = createFileLogger('IDB_BLOAT', { enabled: true });

const SENTRY_BREADCRUMB_CATEGORY = 'idb.bloat';

// Per-prefix HARD limits in bytes. Above this we clear the IDB.
// Mirror wsSizeBudget's table — if the doc exceeds this size locally
// it cannot be sync'd to the server, so it has no purpose in IDB.
const DEFAULT_HARD_LIMITS_BY_PREFIX: Record<string, number> = {
	'org:': 16 * 1024 * 1024,            // 16 MiB
	'doc:': 16 * 1024 * 1024,            // 16 MiB
	'note:': 4 * 1024 * 1024,            // 4 MiB
	'page:': 4 * 1024 * 1024,            // 4 MiB
	'space:': 4 * 1024 * 1024,           // 4 MiB
	'space-chat:': 2 * 1024 * 1024,      // 2 MiB
	'conversation:': 2 * 1024 * 1024,    // 2 MiB
	'notification:': 256 * 1024,         // 256 KiB
	'window-position:': 64 * 1024,       // 64 KiB
	'terminal:': 4 * 1024 * 1024,        // 4 MiB
	'dashboard:': 4 * 1024 * 1024,       // 4 MiB
	'broadcast:': 4 * 1024 * 1024,       // 4 MiB
};

// Catch-all for prefixes we don't explicitly enumerate.
const FALLBACK_HARD_LIMIT_BYTES = 4 * 1024 * 1024;

// Soft warning ratio. Anything between WARN_RATIO * hard and hard fires a
// log + Sentry breadcrumb but does NOT clear IDB (just observability).
const DEFAULT_WARN_RATIO = 0.75;

export type BloatStatus = 'ok' | 'warn' | 'clear';

export interface BloatEvaluation {
	status: BloatStatus;
	prefix: string;
	hardLimitBytes: number;
	warnLimitBytes: number;
	sizeBytes: number;
}

interface SentryLike {
	addBreadcrumb(crumb: {
		category: string;
		message: string;
		level?: 'debug' | 'info' | 'warning' | 'error';
		data?: Record<string, unknown>;
	}): void;
}

function inferPrefix(roomname: string): string {
	const colon = roomname.indexOf(':');
	if (colon === -1) return roomname;
	return roomname.slice(0, colon + 1);
}

function shortRoom(roomName: string): string {
	if (roomName.length <= 18) return roomName;
	return `${roomName.slice(0, 6)}…${roomName.slice(-10)}`;
}

function defaultHardLimit(roomname: string, overrides: Record<string, number>): number {
	const prefix = inferPrefix(roomname);
	return overrides[prefix] ?? DEFAULT_HARD_LIMITS_BY_PREFIX[prefix] ?? FALLBACK_HARD_LIMIT_BYTES;
}

export interface EvaluateOptions {
	/** Override the per-prefix hard limit table. */
	hardLimitsByPrefix?: Record<string, number>;
	/** Override the soft-warn ratio (default 0.75). */
	warnRatio?: number;
}

/**
 * Pure evaluator. Decides whether the IDB-loaded state is fine, large
 * enough to log, or so large the IDB should be cleared.
 *
 * No side effects — call `clearBloatedIdb` separately if status is 'clear'.
 */
export function evaluateLocalSize(
	roomname: string,
	sizeBytes: number,
	options: EvaluateOptions = {},
): BloatEvaluation {
	const overrides = options.hardLimitsByPrefix ?? {};
	const warnRatio = options.warnRatio ?? DEFAULT_WARN_RATIO;
	const hardLimitBytes = defaultHardLimit(roomname, overrides);
	const warnLimitBytes = Math.floor(hardLimitBytes * warnRatio);
	const prefix = inferPrefix(roomname);

	let status: BloatStatus;
	if (sizeBytes >= hardLimitBytes) {
		status = 'clear';
	} else if (sizeBytes >= warnLimitBytes) {
		status = 'warn';
	} else {
		status = 'ok';
	}

	return { status, prefix, hardLimitBytes, warnLimitBytes, sizeBytes };
}

/** Minimal interface so we don't depend directly on y-indexeddb's class. */
export interface ClearableIdb {
	clearData(): Promise<void> | void;
}

export interface ClearOptions {
	sentry?: SentryLike;
	/** Test seam — defaults to a real synthetic broadcast. */
	notify?: (event: { roomShort: string; sizeBytes: number; hardLimitBytes: number }) => void;
}

/**
 * Side-effect: clears IDB and notifies the banner. Returns the promise from
 * `idb.clearData()` so callers can await it if they care.
 */
export async function clearBloatedIdb(
	roomname: string,
	idb: ClearableIdb,
	evaluation: BloatEvaluation,
	options: ClearOptions = {},
): Promise<void> {
	const sentry = options.sentry ?? (SentryDefault as SentryLike);
	const roomShort = shortRoom(roomname);

	log.error(
		`[idbBloatDetector] ${roomShort} IDB bloat detected ` +
		`(${evaluation.sizeBytes}b >= hard=${evaluation.hardLimitBytes}b, prefix=${evaluation.prefix}) ` +
		`— clearing local cache`,
	);

	try {
		sentry.addBreadcrumb({
			category: SENTRY_BREADCRUMB_CATEGORY,
			message: `idb-cleared room=${roomShort} bytes=${evaluation.sizeBytes} hard=${evaluation.hardLimitBytes}`,
			level: 'error',
			data: {
				roomShort,
				prefix: evaluation.prefix,
				sizeBytes: evaluation.sizeBytes,
				hardLimitBytes: evaluation.hardLimitBytes,
			},
		});
	} catch {
		// Sentry must never block the recovery path.
	}

	if (typeof options.notify === 'function') {
		try {
			options.notify({ roomShort, sizeBytes: evaluation.sizeBytes, hardLimitBytes: evaluation.hardLimitBytes });
		} catch {
			// Subscriber faults must not break the IDB clear.
		}
	} else {
		try {
			broadcastSyntheticHardStop({
				roomShort,
				cause: 'idb_bloat_cleared',
				trigger: {
					code: undefined,
					reason: `idb_bloat_bytes=${evaluation.sizeBytes}`,
				},
			});
		} catch {
			// Banner subscribers must not break the IDB clear.
		}
	}

	try {
		await Promise.resolve(idb.clearData());
	} catch (err) {
		log.warn(`[idbBloatDetector] ${roomShort} idb.clearData() failed:`, err);
	}
}

/**
 * Convenience: log a soft-warn breadcrumb when the IDB is approaching
 * but not over the hard limit. Side-effect: log + Sentry, no IDB change.
 */
export function logBloatWarning(
	roomname: string,
	evaluation: BloatEvaluation,
	options: { sentry?: SentryLike } = {},
): void {
	const sentry = options.sentry ?? (SentryDefault as SentryLike);
	const roomShort = shortRoom(roomname);
	log.warn(
		`[idbBloatDetector] ${roomShort} IDB approaching limit ` +
		`(${evaluation.sizeBytes}b >= warn=${evaluation.warnLimitBytes}b, hard=${evaluation.hardLimitBytes}b)`,
	);
	try {
		sentry.addBreadcrumb({
			category: SENTRY_BREADCRUMB_CATEGORY,
			message: `idb-warn room=${roomShort} bytes=${evaluation.sizeBytes} hard=${evaluation.hardLimitBytes}`,
			level: 'warning',
			data: {
				roomShort,
				prefix: evaluation.prefix,
				sizeBytes: evaluation.sizeBytes,
				warnLimitBytes: evaluation.warnLimitBytes,
				hardLimitBytes: evaluation.hardLimitBytes,
			},
		});
	} catch { /* noop */ }
}

/** Test-only export of the default-limit table. */
export const _DEFAULT_HARD_LIMITS_BY_PREFIX = DEFAULT_HARD_LIMITS_BY_PREFIX;
export const _FALLBACK_HARD_LIMIT_BYTES = FALLBACK_HARD_LIMIT_BYTES;
export const _DEFAULT_WARN_RATIO = DEFAULT_WARN_RATIO;
